/**
 * A tela interna do documento: o comercial confere, imprime e pega o link para mandar.
 *
 * A folha em si é o `DocumentoDaProposta`, o mesmo componente que o link público renderiza — o
 * que o cliente vê tem de ser o que o comercial viu.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Snackbar, Stack, Tooltip } from '@mui/material';
import {
  Print as PrintIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import proposalService from '../../services/proposalService';
import { DocumentoDaProposta, FolhaEmBranco } from './components/DocumentoDaProposta';
import type { Proposta } from '../../types/proposal';

/** O endereço que a pessoa vai colar no WhatsApp. Monta-se da origem em que a tela já está. */
function enderecoDoLink(token: string): string {
  return `${window.location.origin}/proposta/${token}`;
}

function quandoBR(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export const ProposalDocumentPage: React.FC = () => {
  const navegar = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [proposta, setProposta] = useState<Proposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    if (id === undefined) return;
    let ativo = true;
    void (async () => {
      try {
        const p = await proposalService.porId(Number(id));
        if (ativo) setProposta(p);
      } catch (err: unknown) {
        if (ativo) {
          setErro(err instanceof Error ? err.message : 'Não foi possível carregar a proposta');
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  /**
   * Copiar o link. `navigator.clipboard` não existe fora de contexto seguro nem em todo
   * navegador — quando falha, o link vai para o aviso, para a pessoa copiar à mão em vez de
   * ficar sem saber o que aconteceu.
   */
  const copiarLink = useCallback(async () => {
    if (!proposta) return;
    try {
      const token = proposta.shareToken ?? (await proposalService.gerarLink(proposta.id));
      const endereco = enderecoDoLink(token);
      setProposta((atual) => (atual ? { ...atual, shareToken: token } : atual));

      try {
        await navigator.clipboard.writeText(endereco);
        setAviso('Link copiado. É só colar para o cliente.');
      } catch {
        setAviso(endereco);
      }
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível gerar o link');
    }
  }, [proposta]);

  if (erro) {
    return (
      <Box p={3}>
        <Alert severity="error">{erro}</Alert>
      </Box>
    );
  }

  if (!proposta) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FolhaEmBranco>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        className="nao-imprime"
        sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navegar('/proposals')}>
          Voltar
        </Button>
        <Button startIcon={<EditIcon />} onClick={() => navegar(`/proposals/${proposta.id}`)}>
          Editar
        </Button>
        <Button startIcon={<LinkIcon />} onClick={() => void copiarLink()}>
          {proposta.shareToken ? 'Copiar link' : 'Gerar link do cliente'}
        </Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Imprimir / Salvar em PDF
        </Button>

        <Box flexGrow={1} />

        {proposta.viewCount > 0 ? (
          <Tooltip
            title={`Primeira vez em ${quandoBR(proposta.firstViewedAt)} · última em ${quandoBR(
              proposta.lastViewedAt
            )}`}
          >
            <Chip
              icon={<VisibilityIcon />}
              color="success"
              variant="outlined"
              label={
                proposta.viewCount === 1
                  ? `Aberta 1 vez · ${quandoBR(proposta.lastViewedAt)}`
                  : `Aberta ${proposta.viewCount} vezes · última em ${quandoBR(proposta.lastViewedAt)}`
              }
            />
          </Tooltip>
        ) : (
          proposta.shareToken && (
            <Chip variant="outlined" label="Link gerado, ainda não aberto" />
          )
        )}
      </Stack>

      <DocumentoDaProposta proposta={proposta} />

      <Snackbar
        open={aviso !== null}
        autoHideDuration={8000}
        onClose={() => setAviso(null)}
        message={aviso ?? ''}
      />
    </FolhaEmBranco>
  );
};

export default ProposalDocumentPage;
